// Greenhouse — recurring operational reminders. No AI, no marketing consultant.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Sprout, Calendar, CheckCircle2, Clock, ExternalLink } from "lucide-react";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import {
  useDueReminders, useAllReminders,
  useCompleteReminder, useSnoozeReminder, useToggleChecklistItem,
  CATEGORY_META, PRIORITY_META, describeRecurrence,
  type GreenhouseReminder,
} from "@/lib/greenhouse";

export default function GreenhousePage() {
  const due = useDueReminders();
  const all = useAllReminders();

  const dueList = due.data?.reminders ?? [];
  const allList = all.data?.reminders ?? [];
  const upcoming = useMemo(
    () => allList.filter((r) => r.status === "active" && !dueList.some((d) => d.id === r.id)),
    [allList, dueList],
  );
  const completed = useMemo(
    () => allList.filter((r) => r.status === "completed"),
    [allList],
  );

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-5xl mx-auto">
      <header className="flex items-start gap-3">
        <Sprout className="h-6 w-6 text-secondary mt-1" />
        <div>
          <div className="eyebrow">The Greenhouse</div>
          <h1 className="font-heading text-3xl text-foreground">Recurring reminders</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Operational tasks that repeat on a schedule. Only reminders that are due show up here.
            Complete one and the next occurrence appears automatically.
          </p>
        </div>
      </header>

      <section>
        <h2 className="font-heading text-lg mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" /> Due now
          <Badge variant="secondary" className="ml-1 text-[10px]">{dueList.length}</Badge>
        </h2>
        {due.isLoading ? (
          <LoadingCard />
        ) : dueList.length === 0 ? (
          <Card className="p-6 text-sm text-muted-foreground">
            Nothing is due today. Enjoy the breathing room.
          </Card>
        ) : (
          <div className="space-y-3">
            {dueList.map((r) => (
              <ReminderCard key={r.id} reminder={r} />
            ))}
          </div>
        )}
      </section>

      {upcoming.length > 0 && (
        <section>
          <h2 className="font-heading text-lg mb-3 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" /> Upcoming
          </h2>
          <Card className="divide-y">
            {upcoming.map((r) => (
              <div key={r.id} className="p-3 flex items-center gap-3 text-sm">
                <span aria-hidden>{CATEGORY_META[r.category]?.emoji ?? "🌿"}</span>
                <span className="font-medium flex-1">{r.title}</span>
                <span className="text-xs text-muted-foreground">
                  {format(parseISO(r.due_date), "MMM d, yyyy")}
                </span>
              </div>
            ))}
          </Card>
        </section>
      )}

      {completed.length > 0 && (
        <section>
          <h2 className="font-heading text-lg mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-secondary" /> Recently completed
          </h2>
          <Card className="divide-y">
            {completed.slice(0, 8).map((r) => (
              <div key={r.id} className="p-3 flex items-center gap-3 text-sm">
                <span aria-hidden>{CATEGORY_META[r.category]?.emoji ?? "🌿"}</span>
                <span className="font-medium flex-1">{r.title}</span>
                <span className="text-xs text-muted-foreground">
                  {r.completed_at ? formatDistanceToNow(parseISO(r.completed_at), { addSuffix: true }) : ""}
                </span>
              </div>
            ))}
          </Card>
        </section>
      )}
    </div>
  );
}

function LoadingCard() {
  return (
    <Card className="p-4 flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading reminders…
    </Card>
  );
}

function ReminderCard({ reminder }: { reminder: GreenhouseReminder }) {
  const complete = useCompleteReminder();
  const snooze = useSnoozeReminder();
  const toggleItem = useToggleChecklistItem();
  const [completing, setCompleting] = useState(false);
  const [notes, setNotes] = useState("");

  const cat = CATEGORY_META[reminder.category];
  const pri = PRIORITY_META[reminder.priority];
  const overdue = new Date(reminder.due_date + "T00:00:00") < new Date(new Date().toDateString());

  return (
    <Card className="p-4 md:p-5 space-y-3">
      <div className="flex items-start gap-3">
        <span className="text-xl" aria-hidden>{cat?.emoji ?? "🌿"}</span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading text-base">{reminder.title}</h3>
            <Badge variant="outline" className={`text-[10px] ${pri.className}`}>{pri.label}</Badge>
            {overdue && <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/30">Overdue</Badge>}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {cat?.label} · Due {format(parseISO(reminder.due_date), "MMM d, yyyy")} · {describeRecurrence(reminder.recurrence)}
          </div>
        </div>
      </div>

      {reminder.description && (
        <p className="text-sm text-foreground/85 whitespace-pre-wrap">{reminder.description}</p>
      )}

      {reminder.checklist.length > 0 && (
        <ul className="space-y-1.5 rounded-md bg-muted/30 p-3">
          {reminder.checklist.map((item, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={!!item.done}
                onCheckedChange={(v) =>
                  toggleItem.mutate({ reminder_id: reminder.id, index: idx, done: !!v })
                }
              />
              <span className={item.done ? "line-through text-muted-foreground" : ""}>{item.label}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {reminder.action_label && reminder.action_href && (
          <Button size="sm" variant="outline" asChild>
            <Link to={reminder.action_href}>
              {reminder.action_label} <ExternalLink className="h-3 w-3 ml-1" />
            </Link>
          </Button>
        )}
        <Button size="sm" onClick={() => setCompleting(true)}>
          <CheckCircle2 className="h-4 w-4 mr-1" /> Mark complete
        </Button>
        <Button
          size="sm" variant="ghost"
          onClick={() => snooze.mutate({ reminder_id: reminder.id, days: 3 })}
          disabled={snooze.isPending}
        >
          Snooze 3 days
        </Button>
      </div>

      <Dialog open={completing} onOpenChange={setCompleting}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete "{reminder.title}"</DialogTitle>
            <DialogDescription>
              {reminder.recurrence
                ? `This will log completion and schedule the next occurrence (${describeRecurrence(reminder.recurrence).toLowerCase()}).`
                : "This will log completion and close this reminder."}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Notes (optional) — link, post, or a quick note"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCompleting(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                await complete.mutateAsync({ reminder_id: reminder.id, notes: notes || undefined });
                setCompleting(false);
                setNotes("");
              }}
              disabled={complete.isPending}
            >
              {complete.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
